import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { OptionsHOC } from '../../hocs'
import { Image } from 'react-bootstrap'
import { Motion, spring } from 'react-motion'
import Velocity from 'velocity-animate'
import PropTypes from 'prop-types'
import 'velocity-animate/velocity.ui'
import { hexToRgbA } from '../../helpers/utils'

const InteractiveDevice = ({
  stepData: { id, button, phoneImage, portrait },
  currentStep,
  playStepAnimation,
  automatedStep,
  createUserEvent,
  updateStep,
  windowDimensions,
  stepData,
  styleBase
}) => {
  const [phoneImageLoaded, setPhoneImageLoaded] = useState(false)
  const [phoneHeight, setPhoneHeight] = useState(0)
  const [phoneWidth, setPhoneWidth] = useState(0)
  const [swipeTimer, setSwipeTimer] = useState(null)

  useEffect(() => {
    toggleSwipeAnimation()
    toggleStepAnimation()
  }, [stepData])

  const toggleSwipeAnimation = (id) => {
    if (!id) return
    stopSwipeAnimation()
    playSwipeAnimation(button, phoneImage, portrait)
  }

  const toggleStepAnimation = () => {
    if (currentStep === automatedStep()) {
      playStepAnimation()
    }
  }

  const handlePhoneLoad = (e) => {
    setPhoneHeight(e.target.naturalHeight)
    setPhoneWidth(e.target.naturalWidth)
    setPhoneImageLoaded(true)
  }

  const calcButtonPosition = (button, phoneImage, portrait) => {
    let top = 0,
      left = 0,
      centerY = 0,
      centerX = 0

    // button not defined
    if (button.lcdButton === 0 && !isSwipe(button)) {
      top = button.yUpperLeft
      left = button.xUpperLeft
    } else if (isSwipe(button) && button.swipeOrAction === 'LEFT') {
      top = button.yUpperLeft + phoneImage.screenYUpperLeft
      left = phoneImage.original.screenXUpperLeft
      // this is a button on the screen, add the screen offset
    } else {
      top = button.yUpperLeft + phoneImage.original.screenYUpperLeft //  + dY)
      left = button.xUpperLeft + phoneImage.original.screenXUpperLeft // + dX)
    }

    // now we have top and left upper corners but need to center the button
    // take the corner and add half the box size and subtract half the radius of the circle

    if (button) {
      centerY = top + button.ySize / 2 - 16
      centerX = left + button.xSize / 2 - 16

      // flip the x and y if the phone is NOT rotated in responsive mode but it would be in regular mode
      // the screen is prevented from rotating but button position still acts as if its horizontal
      const mediaQuery = window.matchMedia('(min-width: 700px)')
      if (!mediaQuery.matches && !portrait) {
        let temp = centerY
        centerY = centerX
        centerX = temp
      }
    }

    // return an object
    return {
      top: centerY,
      left: centerX
    }
  }

  /* does this button contain a swipe?
   * returns a boolean
   */
  const isSwipe = (button) => {
    const swipeOrAction =
      button && button.swipeOrAction ? button.swipeOrAction : null
    let res = false
    switch (swipeOrAction) {
      case 'UP':
      case 'DOWN':
      case 'LEFT':
      case 'RIGHT':
        res = true
        break
      default:
        break
    }

    return res
  }

  /* create a styling around the swipe animation to contain it and set the width
   *  this current its set only for a horizontal swipe positoned 180px down the screen
   *  @todo: It needs adjusted to be positoned at the button location instead
   *  This should probably handle all buttons not just the swipe, right now
   *  the logic is a bit goofy how it switches between button and container
   */
  const getTouchContainerStyle = (button, buttonX, buttonY) => {
    const showTouchContainer = phoneImageLoaded && button.xSize && button.ySize
    const rotation = getTouchContainerRotation(button)

    return {
      cursor: 'pointer',
      width: 32,
      height: 32,
      bottom: false,
      left: false,
      position: 'absolute',
      zIndex: 45,
      msTransformOrigin: '50% 50%',
      transformOrigin: '50% 50%',
      display: showTouchContainer ? 'block' : 'none',
      WebkitTransform: `translate3d(${buttonX}px, ${buttonY}px, 0) rotate(${rotation}deg) `,
      transform: `translate3d(${buttonX}px, ${buttonY}px, 0) rotate(${rotation}deg) `
    }
  }
  // Get rotation value of swipe container
  const getTouchContainerRotation = (button) => {
    const swipeOrAction =
      button && button.swipeOrAction ? button.swipeOrAction : null
    let rotation = 0
    switch (swipeOrAction) {
      case 'DOWN':
        rotation = 180
        break
      case 'LEFT':
        rotation = -90
        break
      case 'RIGHT':
        rotation = 90
        break
      default:
        break
    }

    return rotation
  }

  const playSwipeAnimation = (btn) => {
    // This number is used on backend - when button is set as swipe
    // it's position gets moved 75 points to the oppossite side of animation
    const animationTrailLength = 75

    // the button element in the dom
    const button = document.getElementById('touch')
    let startLeft,
      endLeft,
      startTop,
      endTop = 0
    let trailClass, trailShadow
    const c = hexToRgbA(styleBase['.circle.active'].backgroundColor, 0.8)
    if (btn && btn.swipeOrAction) {
      startTop = -animationTrailLength
      trailClass = 'trail'
      trailShadow = `0px 10px 20px 0px ${c}, 0 30px 20px 0px ${c},  0 50px 20px 0px ${c}, 0 70px 20px 0px ${c}`
    }
    if (btn && btn.swipeOrAction !== 'NULL') {
      // velocityjs forcefeed start values because apparently we can't get the current positioning from the DOM
      const swipeTimer = setInterval(() => {
        Velocity(
          button,
          {
            translateX: [startLeft, endLeft],
            translateY: [startTop, endTop]
          },
          {
            easing: 'ease-in-out',
            duration: 850,
            begin: (el) => {
              el[0].style.transform = ''
              if (trailShadow !== '') el[0].style.boxShadow = trailShadow
              el[0].classList.add(trailClass)
            },
            complete: (el) => {
              el[0].style.transform = ''
              if (trailShadow !== '') el[0].style.boxShadow = ''
              el[0].classList.remove(trailClass)
            }
          }
        )
      }, 1300)
      // add the timer to the react state so it can be cleared later
      setSwipeTimer(swipeTimer)
    }
  }

  /* stop the currently playing animation */
  const stopSwipeAnimation = () => {
    const button = document.getElementById('touch')
    if (button) {
      button.style.transform = ''
      button.top = 0
      button.left = 0
      button.style.boxShadow = ''
      button.classList.remove('trail')
      button.classList.remove('trail-up')
      button.classList.remove('trail-down')
      button.classList.remove('trail-left')
    }

    clearInterval(swipeTimer)
  }

  const handleOnPhoneClick = () => {
    if (stepData.isPlaying) {
      return null
    }

    createUserEvent({
      category: 'tutorials',
      data: {
        action: 'OnDeviceClick',
        target: stepData.id,
        step_to: stepData.steps.currentId + 1
      }
    })
    windowDimensions.width > 700
      ? playStepAnimation()
      : updateStep(stepData.steps.nextStepId)
  }

  const DeviceContainer = styled.div`
    padding-left: 30%;

    @media only screen and (max-width: 768px) {
      padding-left: 0%;
    }
  `

  const phoneOffset = stepData.portrait || windowDimensions.width <= 700
    ? 0 : (phoneHeight - phoneWidth) / 2
  const lcdStyle = {
    top: stepData.phoneImage.screenYUpperLeft - phoneHeight,
    left: stepData.phoneImage.screenXUpperLeft,
    width: stepData.phoneImage.screenXSize,
    height: stepData.phoneImage.screenYSize,
    marginBottom: stepData.phoneImage.screenYUpperLeft - phoneHeight,
    display: 'block',
    position: 'relative',
    float: 'left',
    zIndex: stepData.phoneImage.faceplate_behind === 1 ? 3 : 1
  }
  const touchHighlightColor = {
    backgroundColor: hexToRgbA(
      styleBase['.circle.active'].backgroundColor,
      0.8
    ),
    border: `2px solid ${styleBase['.circle.active'].color}`
  }
  let buttonPosition = calcButtonPosition(
    stepData.button,
    stepData.phoneImage,
    stepData.portrait
  )
  const rotateDevice = !stepData.portrait && windowDimensions.width >= 700

  return (
    <DeviceContainer>
      <Motion style={{ rotate: spring(rotateDevice ? -90 : 0), x: spring(phoneOffset), y: spring(phoneOffset) }}>
        {({ rotate, x, y }) => (
          <div className="phone-container">
            <div
              className="phone-image-container"
              style={{
                width: phoneWidth,
                marginBottom: rotateDevice ? phoneWidth - phoneHeight : 0,
                WebkitTransform: `rotate(${rotate}deg) translate3d(${x}px, ${y}px, 0)`,
                transform: `rotate(${rotate}deg) translate3d(${x}px, ${y}px, 0)`,
                float: 'left'
              }}
            >
              <Image
                style={{ position: 'relative', zIndex: 2 }}
                id="phone-image"
                ref="phoneImage"
                onLoad={handlePhoneLoad}
                src={stepData.phoneImage.imageLocation}
              />
              {phoneImageLoaded && stepData.lcdImage && (
                <Image
                  style={lcdStyle}
                  className="lcd-image"
                  responsive
                  src={stepData.lcdImage}
                />
              )}
            </div>
            {buttonPosition.left >= -25 && buttonPosition.top >= -25 && (
              <Motion
                style={{
                  buttonX: spring(buttonPosition.left),
                  buttonY: spring(buttonPosition.top)
                }}
              >
                {({ buttonX, buttonY }) => (
                  <div
                    style={getTouchContainerStyle(
                      stepData.button,
                      buttonX,
                      buttonY
                    )}
                    onClick={handleOnPhoneClick}
                  >
                    <button
                      id="touch"
                      className="touch"
                      style={touchHighlightColor}
                    />
                  </div>
                )}
              </Motion>
            )}
          </div>
        )}
      </Motion>
    </DeviceContainer>
  )
}
InteractiveDevice.propTypes = {
  currentStep: PropTypes.number.isRequired,
  playStepAnimation: PropTypes.func,
  automatedStep: PropTypes.func,
  createUserEvent: PropTypes.func,
  updateStep: PropTypes.func,
  deviceName: PropTypes.string,
  windowDimensions: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number
  }).isRequired,
  stepData: PropTypes.shape({
    id: PropTypes.number,
    portrait: PropTypes.bool,
    isPlaying: PropTypes.bool,
    lcdImage: PropTypes.string,

    button: PropTypes.shape({
      lcdButton: PropTypes.number,
      swipeOrAction: PropTypes.string,
      xSize: PropTypes.number,
      ySize: PropTypes.number,
      xUpperLeft: PropTypes.number,
      yUpperLeft: PropTypes.number
    }),

    phoneImage: PropTypes.shape({
      imageLocation: PropTypes.string,
      screenXSize: PropTypes.number,
      screenYSize: PropTypes.number,
      screenXUpperLeft: PropTypes.number,
      screenYUpperLeft: PropTypes.number,
      original: PropTypes.shape({
        screenXUpperLeft: PropTypes.number,
        screenYUpperLeft: PropTypes.number
      })
    }),

    steps: PropTypes.shape({
      currentId: PropTypes.number,
      nextStepId: PropTypes.number
    })
  })
}

export default OptionsHOC(InteractiveDevice)
